
import { offlineDb } from '@/db/offlineDb';
import { supabase } from "@/integrations/supabase/client";
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';

/**
 * Get the maximum position value for items in a shopping list
 * with offline support
 */
export const getMaxPosition = async (listId: string): Promise<number | null> => {
  try {
    if (networkService.isOnline()) {
      // 🟢 ONLINE: try Supabase first
      try {
        const { data, error } = await supabase
          .from("shopping_items")
          .select("position")
          .eq("list_id", listId)
          .order("position", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          OfflineLogger.error("getMaxPosition", `Online error: ${error.message}`);
          // Fallback to offline
          return getMaxPositionOffline(listId);
        }

        const maxPosition = data?.position ?? null;
        OfflineLogger.info("getMaxPosition", `🟢 Online max position: ${maxPosition}`);
        return maxPosition;
      } catch (err) {
        OfflineLogger.error("getMaxPosition", `Online fetch failed: ${err}`);
        // Fallback to offline
        return getMaxPositionOffline(listId);
      }
    } else {
      // 🔴 OFFLINE: use local database
      return getMaxPositionOffline(listId);
    }
  } catch (err) {
    OfflineLogger.error("getMaxPosition", `General error: ${err}`);
    return null;
  }
};

/**
 * Get max position from offline database
 */
const getMaxPositionOffline = async (listId: string): Promise<number | null> => {
  try {
    const items = await offlineDb.shoppingItems
      .where('list_id')
      .equals(listId)
      .and(item => item._syncStatus !== 'deleted')
      .toArray();

    if (items.length === 0) {
      OfflineLogger.info("getMaxPosition", "🔴 No offline items found");
      return null;
    }

    const maxPosition = Math.max(...items.map(item => item.position || 0));
    OfflineLogger.info("getMaxPosition", `🔴 Offline max position: ${maxPosition}`);
    return maxPosition;
  } catch (err) {
    OfflineLogger.error("getMaxPosition", `Offline error: ${err}`);
    return null;
  }
};
